import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CompanyProjectFormSchema } from "./schema";
import { useAuth0 } from "@auth0/auth0-react";
import request from "@/lib/api/handler";
import localDateToDateString from "@/utils/localDateToDateString";
import { CompanyJobOpening } from "@/types";
import { QueryKeys, useInvalidateQuery } from "@/queries/utils";
import { toast } from "sonner";

export const useCompanyOpportunityDraftProjectMutation = (
  id?: string,
  companyId?: string,
) => {
  const qc = useQueryClient();
  const invalidate = useInvalidateQuery();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async ({
      data,
      mode,
    }: {
      data: CompanyProjectFormSchema;
      mode: "create" | "update" | "publish";
      onSuccess?: (data: CompanyJobOpening) => void;
    }) => {
      if (mode !== "create" && !id) {
        throw new Error("No ID provided for update operation");
      }

      const token = await getAccessTokenSilently();
      const body = buildFormData(data, mode);

      const response: CompanyJobOpening = await request({
        method: mode === "create" ? "POST" : "PATCH",
        token,
        path: mode === "create" ? `/api/jobOpening/` : `/api/jobOpening/${id}`,
        params:
          mode === "create"
            ? {
                companyId,
                draft: true,
              }
            : mode === "publish"
              ? {
                  publish: true,
                }
              : {},
        body,
        contentType: "application/x-www-form-urlencoded",
      });

      return response;
    },
    async onSuccess(data, variables) {
      if (variables.mode !== "create") {
        toast.success(
          `Opportunity ${variables.mode === "publish" ? "created" : "updated"} successfully!`,
        );
      }

      variables.onSuccess?.(data);

      qc.setQueryData(QueryKeys.companyOpportunity(data.id), data);

      await invalidate(QueryKeys.companyOpportunities);
      await invalidate(QueryKeys.adminJobDetails(data.id));

      if (variables.mode === "publish") {
        await invalidate(QueryKeys.companyDraftJobDescriptions(data.id));
      }
    },
  });
};

function buildFormData(
  data: CompanyProjectFormSchema,
  mode: "create" | "update" | "publish",
) {
  const body = new FormData();

  if (mode === "create") {
    body.append("type", "Project");
  }

  body.append("role", data.jobTitle);
  body.append("scopeEngagementDuration", data.scopeEngagementDuration);
  body.append("budgetType", data.budgetType);
  body.append("budgetMinCents", data.budgetMinCents.toString());
  body.append("budgetMaxCents", data.budgetMaxCents.toString());
  body.append("budgetNotes", data.budgetNotes ?? "");
  body.append("desiredStartDate", localDateToDateString(data.idealStartDate));
  body.append("shortDescription", data.description);
  body.append("deliverables", data.deliverables);
  body.append("goals", data.goals);
  body.append("matchCriteria1", data.matchCriteria[0] ?? "");
  body.append("matchCriteria2", data.matchCriteria[1] ?? "");
  body.append("matchCriteria3", data.matchCriteria[2] ?? "");
  body.append("noteForVenturous", data.additionalInformation ?? "");

  return body;
}
